/**
 * Skills editor — wireado contra /api/skills.
 *
 * Vista única con tres modos según selección:
 *  - Sin skill seleccionado → lista de skills del cliente + botón "Crear manual".
 *  - Con skill seleccionado → textarea grande con contenido de la versión
 *    productiva, botón "Guardar nueva versión", sidebar con histórico.
 *  - Click en "Ver diff vs producción" sobre una versión → modal con diff
 *    visual side-by-side (verde adds, rojo deletes).
 *  - Click "Promover" → confirmación + POST promote.
 */
import { useEffect, useState } from "react";
import { useParams } from "@tanstack/react-router";
import {
  Brain,
  Check,
  FileText,
  GitCompare,
  Plus,
  Save,
  Star,
  X,
} from "lucide-react";
import {
  useSkillsForClient,
  useSkill,
  useCreateSkill,
  useCreateSkillVersion,
  usePromoteVersion,
  useSkillDiff,
  type SkillRow,
  type SkillVersionRow,
} from "@/hooks/use-skills";
import { findClient } from "@/lib/clients";
import { EmptyState } from "@/components/shared/empty-state";
import { DiffViewer } from "@/components/skills/diff-viewer";
import { cn } from "@/lib/cn";
import type { ClientSlug } from "@/types";

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString("es-AR", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export function SkillsPage() {
  const { clientSlug } = useParams({ strict: false }) as { clientSlug?: string };
  const client = findClient(clientSlug);
  const [selectedSkillId, setSelectedSkillId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const { data: skills, isLoading } = useSkillsForClient(client?.slug);

  if (!client) {
    return (
      <div className="p-7 min-h-full bg-background">
        <EmptyState
          title="Seleccioná un cliente"
          description="Los manuales de moderación viven por cliente desde su workspace."
        />
      </div>
    );
  }

  return (
    <div className="min-h-full bg-background overflow-y-auto">
      <div className="max-w-6xl mx-auto px-7 py-8">
        <header className="mb-6 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-sans text-3xl font-semibold tracking-tight text-text-primary">
              Manuales de moderación
            </h1>
            <p className="font-sans text-sm text-text-muted mt-1">
              {client.fullName} · cada cambio queda versionado y auditado
            </p>
          </div>
          {!showCreate && (
            <button
              type="button"
              onClick={() => setShowCreate(true)}
              className="inline-flex items-center gap-2 h-9 px-3.5 rounded-lg bg-magenta text-white hover:bg-magenta-hover transition-colors font-sans text-sm font-medium"
            >
              <Plus size={14} strokeWidth={2} />
              Crear manual
            </button>
          )}
        </header>

        {showCreate && (
          <CreateSkillForm
            clientSlug={client.slug as ClientSlug}
            onClose={() => setShowCreate(false)}
            onCreated={(skill) => {
              setSelectedSkillId(skill.id);
              setShowCreate(false);
            }}
          />
        )}

        {isLoading ? (
          <p className="text-sm text-text-muted">Cargando manuales…</p>
        ) : !skills?.length ? (
          <EmptyState
            icon={<Brain size={32} strokeWidth={1.5} />}
            title="Sin manuales todavía"
            description="Crear un manual con el botón de arriba. Una vez creado podés cargar versiones nuevas desde el panel — sin tocar el repo."
          />
        ) : selectedSkillId ? (
          <SkillEditor
            skillId={selectedSkillId}
            onBack={() => setSelectedSkillId(null)}
          />
        ) : (
          <SkillsList
            skills={skills}
            onSelect={(id) => setSelectedSkillId(id)}
          />
        )}
      </div>
    </div>
  );
}

function CreateSkillForm({
  clientSlug,
  onClose,
  onCreated,
}: {
  clientSlug: ClientSlug;
  onClose: () => void;
  onCreated: (skill: SkillRow) => void;
}) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const create = useCreateSkill();

  function handleSubmit() {
    if (!name.trim()) return;
    create.mutate(
      { clientSlug, name: name.trim(), description: description.trim() || undefined },
      { onSuccess: onCreated },
    );
  }

  return (
    <div className="card-surface p-5 mb-6">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-sans text-sm font-semibold text-text-primary">
          Nuevo manual
        </h3>
        <button
          type="button"
          onClick={onClose}
          className="inline-flex items-center justify-center w-7 h-7 rounded-md text-text-muted hover:text-text-primary hover:bg-surface-2 transition-colors"
        >
          <X size={14} strokeWidth={1.75} />
        </button>
      </div>
      <div className="space-y-3">
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Nombre del manual (ej: Moderación DMs, Comentarios públicos)"
          className="w-full h-9 px-3 text-sm font-sans rounded-md bg-surface-2 border border-default text-text-primary placeholder:text-text-muted focus:border-magenta focus:outline-none"
        />
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Descripción opcional"
          rows={2}
          className="w-full px-3 py-2 text-sm font-sans rounded-md bg-surface-2 border border-default text-text-primary placeholder:text-text-muted focus:border-magenta focus:outline-none resize-none"
        />
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!name.trim() || create.isPending}
            className="h-9 px-3.5 rounded-lg bg-magenta text-white hover:bg-magenta-hover transition-colors font-sans text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {create.isPending ? "Creando…" : "Crear"}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="h-9 px-3 rounded-lg text-sm font-sans text-text-muted hover:text-text-primary transition-colors"
          >
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
}

function SkillsList({
  skills,
  onSelect,
}: {
  skills: SkillRow[];
  onSelect: (id: string) => void;
}) {
  return (
    <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {skills.map((sk) => (
        <li key={sk.id}>
          <button
            type="button"
            onClick={() => onSelect(sk.id)}
            className="card-surface p-4 w-full text-left hover:shadow-md hover:border-strong transition-all"
          >
            <div className="flex items-center gap-2 mb-1">
              <FileText size={14} strokeWidth={1.75} className="text-text-muted" />
              <h3 className="font-sans text-md font-semibold text-text-primary truncate">
                {sk.name}
              </h3>
            </div>
            {sk.description && (
              <p className="font-sans text-xs text-text-muted line-clamp-2 mt-1">
                {sk.description}
              </p>
            )}
            <div className="mt-3 font-mono text-2xs text-text-muted">
              Actualizado {formatDate(sk.updatedAt)}
            </div>
          </button>
        </li>
      ))}
    </ul>
  );
}

function SkillEditor({
  skillId,
  onBack,
}: {
  skillId: string;
  onBack: () => void;
}) {
  const { data, isLoading } = useSkill(skillId);
  const createVersion = useCreateSkillVersion();
  const [draft, setDraft] = useState<string>("");
  const [notes, setNotes] = useState("");
  const [diffVersionId, setDiffVersionId] = useState<string | null>(null);

  // Hidratar el draft con la versión productiva SOLO la primera vez (draft
  // vacío). Si el operador ya empezó a editar y entra una nueva versión por
  // refetch (ej: otro tab promovió), preservamos su borrador para no perder
  // trabajo. El operador puede recargar manualmente si quiere descartar.
  useEffect(() => {
    if (draft) return; // operador ya está editando — no pisar
    if (data?.productionVersion) {
      setDraft(data.productionVersion.content);
    } else if (data?.versions[0]) {
      setDraft(data.versions[0].content);
    }
  }, [data?.productionVersion?.id, data?.versions[0]?.id, draft]);

  if (isLoading || !data) {
    return <p className="text-sm text-text-muted">Cargando manual…</p>;
  }

  const productionContent = data.productionVersion?.content ?? "";
  const hasChanges = draft !== productionContent;

  function handleSave() {
    if (!hasChanges) return;
    createVersion.mutate(
      { skillId, content: draft, notes: notes.trim() || undefined },
      {
        onSuccess: () => {
          setNotes("");
        },
      },
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-5">
      {/* Editor */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <button
            type="button"
            onClick={onBack}
            className="inline-flex items-center gap-1.5 font-sans text-xs text-text-muted hover:text-text-primary"
          >
            ← Volver a la lista
          </button>
          <div className="flex items-center gap-2">
            <h2 className="font-sans text-lg font-semibold text-text-primary">
              {data.skill.name}
            </h2>
            {data.productionVersion && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-2xs font-sans font-medium bg-success-bg text-success-text border border-success-border">
                <Star size={10} strokeWidth={2} />v{data.productionVersion.versionNumber} en producción
              </span>
            )}
          </div>
        </div>

        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          rows={24}
          spellCheck={false}
          className="w-full px-4 py-3 font-mono text-xs leading-relaxed rounded-xl bg-surface-1 border border-default text-text-primary placeholder:text-text-muted focus:border-magenta focus:outline-none resize-y min-h-[400px]"
          placeholder="Escribí el manual en Markdown…"
        />

        <div className="mt-3 space-y-3">
          <input
            type="text"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Nota del cambio (qué editaste, por qué)"
            className="w-full h-9 px-3 text-sm font-sans rounded-md bg-surface-2 border border-default text-text-primary placeholder:text-text-muted focus:border-magenta focus:outline-none"
          />
          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={handleSave}
              disabled={!hasChanges || createVersion.isPending}
              className="inline-flex items-center gap-2 h-9 px-3.5 rounded-lg bg-magenta text-white hover:bg-magenta-hover transition-colors font-sans text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save size={14} strokeWidth={2} />
              {createVersion.isPending ? "Guardando…" : "Guardar nueva versión"}
            </button>
            {hasChanges && (
              <button
                type="button"
                onClick={() => setDiffVersionId("draft")}
                className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-default text-text-secondary hover:bg-surface-2 hover:text-text-primary transition-colors font-sans text-sm font-medium"
              >
                <GitCompare size={13} strokeWidth={1.75} />
                Ver diff vs producción
              </button>
            )}
            {!hasChanges && (
              <span className="font-sans text-xs text-text-muted">
                Sin cambios pendientes
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Sidebar de versiones */}
      <aside>
        <h3 className="font-sans text-xs font-semibold uppercase tracking-wider text-text-muted mb-2 px-1">
          Versiones
        </h3>
        <ul className="space-y-1.5 max-h-[600px] overflow-y-auto pr-1">
          {data.versions.map((v) => (
            <VersionRow
              key={v.id}
              version={v}
              skillId={skillId}
              isProduction={v.id === data.skill.productionVersionId}
              onCompare={() => setDiffVersionId(v.id)}
            />
          ))}
        </ul>
      </aside>

      {/* Diff modal */}
      {diffVersionId && (
        <DiffModal
          skillId={skillId}
          productionContent={productionContent}
          productionVersionNumber={data.productionVersion?.versionNumber ?? null}
          target={diffVersionId === "draft" ? "draft" : diffVersionId}
          draftContent={draft}
          baseVersionId={data.skill.productionVersionId ?? data.versions[0]?.id ?? null}
          onClose={() => setDiffVersionId(null)}
        />
      )}
    </div>
  );
}

function VersionRow({
  version,
  skillId,
  isProduction,
  onCompare,
}: {
  version: SkillVersionRow;
  skillId: string;
  isProduction: boolean;
  onCompare: () => void;
}) {
  const promote = usePromoteVersion();
  const [confirming, setConfirming] = useState(false);

  function handlePromote() {
    if (!confirming) {
      setConfirming(true);
      return;
    }
    promote.mutate(
      { skillId, versionId: version.id },
      { onSuccess: () => setConfirming(false) },
    );
  }

  return (
    <li className="card-surface p-3">
      <div className="flex items-center justify-between gap-2 mb-1">
        <div className="flex items-center gap-1.5">
          <span className="font-mono text-xs font-semibold text-text-primary">
            v{version.versionNumber}
          </span>
          {isProduction && (
            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-sans font-medium bg-success-bg text-success-text border border-success-border">
              <Star size={8} strokeWidth={2} />
              prod
            </span>
          )}
        </div>
      </div>
      <div className="font-mono text-2xs text-text-muted mb-2">
        {formatDate(version.createdAt)}
      </div>
      {version.changeNote && (
        <p className="font-sans text-xs text-text-secondary line-clamp-2 mb-2">
          {version.changeNote}
        </p>
      )}
      <div className="flex items-center gap-1">
        {!isProduction && (
          <button
            type="button"
            onClick={handlePromote}
            disabled={promote.isPending}
            className={cn(
              "inline-flex items-center gap-1 h-7 px-2 rounded-md font-sans text-2xs font-medium border transition-colors",
              confirming
                ? "border-magenta bg-magenta text-white hover:bg-magenta-hover"
                : "border-default text-text-secondary hover:bg-surface-2 hover:text-text-primary",
            )}
          >
            <Check size={11} strokeWidth={2} />
            {confirming ? "Confirmar" : "Promover"}
          </button>
        )}
        <button
          type="button"
          onClick={onCompare}
          className="inline-flex items-center gap-1 h-7 px-2 rounded-md border border-default text-text-secondary hover:bg-surface-2 hover:text-text-primary font-sans text-2xs font-medium transition-colors"
        >
          <GitCompare size={11} strokeWidth={1.75} />
          Diff
        </button>
      </div>
    </li>
  );
}

function DiffModal({
  skillId,
  target,
  draftContent,
  baseVersionId,
  productionContent,
  productionVersionNumber,
  onClose,
}: {
  skillId: string;
  target: string;
  draftContent: string;
  baseVersionId: string | null;
  productionContent: string;
  productionVersionNumber: number | null;
  onClose: () => void;
}) {
  const isDraft = target === "draft";
  const compareVersionId = isDraft ? undefined : target;

  // Solo llamamos al backend si comparamos dos versiones almacenadas.
  const { data: diff } = useSkillDiff(
    isDraft ? undefined : skillId,
    !isDraft ? baseVersionId ?? undefined : undefined,
    compareVersionId,
  );

  const beforeContent = isDraft ? productionContent : diff?.vA.content ?? "";
  const afterContent = isDraft ? draftContent : diff?.vB.content ?? "";

  const beforeLabel = isDraft
    ? `v${productionVersionNumber ?? "—"} (producción)`
    : `v${diff?.vA.versionNumber ?? "—"} (producción)`;
  const afterLabel = isDraft
    ? "Tu edición sin guardar"
    : `v${diff?.vB.versionNumber ?? "—"}`;

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="card-surface w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-subtle">
          <h3 className="font-sans text-md font-semibold text-text-primary">
            Comparación de versiones
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Cerrar"
            className="inline-flex items-center justify-center w-7 h-7 rounded-md text-text-muted hover:text-text-primary hover:bg-surface-2 transition-colors"
          >
            <X size={14} strokeWidth={1.75} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-5">
          {isDraft || diff ? (
            <DiffViewer
              before={beforeContent}
              after={afterContent}
              beforeLabel={beforeLabel}
              afterLabel={afterLabel}
            />
          ) : (
            <p className="text-sm text-text-muted">Cargando diff…</p>
          )}
        </div>
      </div>
    </div>
  );
}
